/**
 * Layer 1 — context-based steering, 60Hz. (§10.1)
 *
 * "Context-based steering (NOT boids, NOT raw pathfinding — the pitch is open)."
 * The brief is right on both counts, and it is worth saying why, because both
 * wrong answers are tempting:
 *
 *   - Pathfinding solves obstacle avoidance in a maze. A football pitch has no
 *     maze. A* over a grid would produce a corridor-following jog around
 *     opponents who have already moved by the time the path is walked.
 *   - Boids solve flocking. We do not want a flock: eleven players who cohere
 *     and align are a swarm chasing the ball, which is precisely the thing that
 *     makes bad football AI look like bad football AI.
 *
 * Context steering solves the actual problem: score every direction, subtract
 * what is bad about it, go the best way. It re-decides from scratch every tick,
 * so it never commits to a stale plan, and it composes — "get to that space",
 * "hold your shape", "don't run into your own striker" and "stay onside" are
 * four independent contributions to one array of 16 numbers.
 *
 * ── The output is an InputCommand's moveX/moveZ. That is the whole point. ────
 *
 * We do not write a position, a velocity, or a path. We write the same two axes
 * a human's WASD writes, and the SAME player controller (§3.3) turns them into
 * motion — with the same momentum, the same turn-rate-vs-speed curve, the same
 * stamina. An AI physically cannot out-turn or out-accelerate a human, because
 * there is no code path in which it could. (§10.1)
 *
 * Determinism: dcos/dsin only, no Math.* trig, no allocation after module load.
 */

import type { WorldState } from '../sim/types.js';
import { MAX_PLAYERS } from '../sim/types.js';
import {
  AI_CONTEXT_DIRECTIONS,
  AI_ONSIDE_MARGIN,
  AI_OPPONENT_AVOID_RADIUS,
  AI_STEER_MIN_SCORE,
  AI_STEER_ONSIDE_RECOVER_WEIGHT,
  AI_STEER_OPPONENT_AVOID_WEIGHT,
  AI_STEER_SEEK_WEIGHT,
  AI_STEER_TEAMMATE_AVOID_WEIGHT,
  AI_TEAMMATE_AVOID_RADIUS,
} from '../sim/constants.js';
import { clamp, clamp01, dcos, dsin, TWO_PI } from '../math/vec3.js';

const N = AI_CONTEXT_DIRECTIONS;

/**
 * The direction context map's axes, built once at module load.
 *
 * The zero-snap is not cosmetic. dcos(π/2) is 6.1e-17, not 0 — so a naive
 * `dirX[k] > 0` test would classify the two exactly-perpendicular slots as
 * "forward" and the onside mask below would forbid running ALONG the offside
 * line. That is the single direction a striker holding the last defender's
 * shoulder needs most, and the bug would read as "the AI strikers refuse to
 * move" — nothing about the symptom would point at a rounding residue.
 */
const dirX = new Float64Array(N);
const dirZ = new Float64Array(N);
for (let k = 0; k < N; k++) {
  const a = (TWO_PI * k) / N;
  const cx = dcos(a);
  const cz = dsin(a);
  dirX[k] = cx > -1e-12 && cx < 1e-12 ? 0 : cx;
  dirZ[k] = cz > -1e-12 && cz < 1e-12 ? 0 : cz;
}

/** Scratch context maps. Module-scope: the 60Hz loop allocates nothing (§15.7). */
const interest = new Float64Array(N);
const danger = new Float64Array(N);
const blocked = new Uint8Array(N);

/** Movement axes for an InputCommand. Both end up inside [-1, 1]. */
export interface MoveOut {
  x: number;
  z: number;
}

export function createMoveOut(): MoveOut {
  return { x: 0, z: 0 };
}

/**
 * One steering query. Preallocated by the caller and refilled per player per
 * tick — a parameter object rather than 12 positional arguments, and free
 * because it never leaves the caller's field.
 */
export interface SteerRequest {
  id: number;
  /** Where Layer 2 wants this player to go. */
  seekX: number;
  seekZ: number;
  /** Where Layer 3's formation solver says he belongs. */
  shapeX: number;
  shapeZ: number;
  /** Pull toward the anchor. Scaled by difficulty discipline (§10.3). */
  shapeWeight: number;
  /** Ease off inside this distance of the seek target. */
  arriveRadius: number;
  /** Is the offside law live for this player right now? */
  onsideActive: boolean;
  /** +1 if this team attacks +X, −1 otherwise. */
  attackSign: number;
  /** Attack-axis projection this player must not exceed. */
  onsideLimitProj: number;
}

export function createSteerRequest(): SteerRequest {
  return {
    id: 0,
    seekX: 0,
    seekZ: 0,
    shapeX: 0,
    shapeZ: 0,
    shapeWeight: 0,
    arriveRadius: 1,
    onsideActive: false,
    attackSign: 1,
    onsideLimitProj: 0,
  };
}

/**
 * Score the 16-direction context map and resolve it to movement axes.
 *
 * Weighted by, per §10.1: seek target (+), maintain shape (+), avoid teammates
 * (−), avoid opponents (−), stay onside (hard constraint).
 *
 * Note the asymmetry between the two avoidance radii, which is deliberate:
 * teammates are avoided at 3.5m because two AI players occupying the same blade
 * of grass is the classic tell, while opponents are avoided at only 1.5m because
 * an AI that steers around opponents will not press, will not mark, and will not
 * challenge for anything. You are supposed to run at opponents.
 */
export function steer(state: WorldState, req: SteerRequest, out: MoveOut): void {
  const p = state.players;
  const id = req.id;
  const px = p.px[id]!;
  const pz = p.pz[id]!;

  interest.fill(0);
  danger.fill(0);
  blocked.fill(0);

  // ── Seek (+) ────────────────────────────────────────────────────────────
  let sx = req.seekX - px;
  let sz = req.seekZ - pz;
  const seekDist = Math.sqrt(sx * sx + sz * sz);
  if (seekDist > 1e-6) {
    sx /= seekDist;
    sz /= seekDist;
    for (let k = 0; k < N; k++) {
      const d = dirX[k]! * sx + dirZ[k]! * sz;
      if (d > 0) interest[k]! += d * AI_STEER_SEEK_WEIGHT;
    }
  }

  // ── Maintain shape (+) ──────────────────────────────────────────────────
  if (req.shapeWeight > 0) {
    let hx = req.shapeX - px;
    let hz = req.shapeZ - pz;
    const hd = Math.sqrt(hx * hx + hz * hz);
    if (hd > 1e-6) {
      hx /= hd;
      hz /= hd;
      for (let k = 0; k < N; k++) {
        const d = dirX[k]! * hx + dirZ[k]! * hz;
        if (d > 0) interest[k]! += d * req.shapeWeight;
      }
    }
  }

  // ── Avoid teammates (−) and opponents (−) ───────────────────────────────
  const team = p.team[id]!;
  for (let j = 0; j < MAX_PLAYERS; j++) {
    if (j === id || p.active[j] !== 1 || p.sentOff[j] === 1) continue;
    const mate = p.team[j]! === team;
    const radius = mate ? AI_TEAMMATE_AVOID_RADIUS : AI_OPPONENT_AVOID_RADIUS;
    let ox = p.px[j]! - px;
    let oz = p.pz[j]! - pz;
    const d = Math.sqrt(ox * ox + oz * oz);
    if (d >= radius || d < 1e-6) continue;
    ox /= d;
    oz /= d;
    // Linear proximity: a body 3.4m away barely registers, one at arm's length
    // dominates. Squaring it here would make the avoidance snap on rather than
    // build, and AI players would visibly flinch apart.
    const prox = (1 - d / radius) * (mate ? AI_STEER_TEAMMATE_AVOID_WEIGHT : AI_STEER_OPPONENT_AVOID_WEIGHT);
    for (let k = 0; k < N; k++) {
      const dot = dirX[k]! * ox + dirZ[k]! * oz;
      if (dot > 0) danger[k]! += dot * prox;
    }
  }

  // ── Stay onside (HARD constraint) ───────────────────────────────────────
  //
  // Not a weight. A weight loses: the pull of an open goal 20m away will always
  // out-score a soft "maybe don't". Forward directions are MASKED OUT entirely,
  // which is what makes an AI line hold — and what makes an AI striker check his
  // run instead of drifting a metre offside and killing the move.
  //
  // Offside is judged at the instant the ball is PLAYED (§5.4, D8), so this is
  // the AI being careful about a snapshot that has not been taken yet: it holds
  // the line continuously so it is onside whenever the pass actually comes.
  if (req.onsideActive) {
    const proj = req.attackSign * px;
    const limit = req.onsideLimitProj;
    if (proj > limit - AI_ONSIDE_MARGIN) {
      for (let k = 0; k < N; k++) {
        if (req.attackSign * dirX[k]! > 0) blocked[k] = 1;
      }
      if (proj > limit) {
        // Already beyond the line: getting back is now the priority, and it
        // outranks the seek target rather than competing with it.
        for (let k = 0; k < N; k++) {
          const back = -req.attackSign * dirX[k]!;
          if (back > 0) interest[k]! += back * AI_STEER_ONSIDE_RECOVER_WEIGHT;
        }
      }
    }
  }

  // ── Resolve ─────────────────────────────────────────────────────────────
  let best = -1;
  let bestScore = Number.NEGATIVE_INFINITY;
  for (let k = 0; k < N; k++) {
    if (blocked[k] === 1) continue;
    const s = interest[k]! - danger[k]!;
    if (s > bestScore) {
      bestScore = s;
      best = k;
    }
  }

  if (best < 0 || bestScore <= AI_STEER_MIN_SCORE) {
    // Every direction is blocked or worthless. Standing still is a legitimate
    // football decision and the deceleration curve (§3.3) makes it look like one.
    out.x = 0;
    out.z = 0;
    return;
  }

  // Blend toward the better neighbouring slot so movement is not quantized to
  // 22.5° steps. Without this, an AI tracking a target across a slot boundary
  // visibly stair-steps — the turn-rate curve smooths it, but not enough.
  let bx = dirX[best]!;
  let bz = dirZ[best]!;
  const kPrev = (best + N - 1) % N;
  const kNext = (best + 1) % N;
  const sPrev = blocked[kPrev] === 1 ? Number.NEGATIVE_INFINITY : interest[kPrev]! - danger[kPrev]!;
  const sNext = blocked[kNext] === 1 ? Number.NEGATIVE_INFINITY : interest[kNext]! - danger[kNext]!;
  const nb = sPrev > sNext ? kPrev : kNext;
  const nbScore = sPrev > sNext ? sPrev : sNext;
  if (nbScore > 0) {
    // w < 0.5 by construction (nbScore <= bestScore), so the blend can never
    // cross past the neighbour and the denominator is strictly positive.
    const w = nbScore / (bestScore + nbScore);
    bx = bx * (1 - w) + dirX[nb]! * w;
    bz = bz * (1 - w) + dirZ[nb]! * w;
    const len = Math.sqrt(bx * bx + bz * bz);
    if (len > 1e-6) {
      bx /= len;
      bz /= len;
    }
  }

  // Ease in, so the AI settles onto its target instead of orbiting it. A
  // half-magnitude axis stays a half-magnitude axis through readIntent() —
  // the controller clamps, never normalizes (§4.1) — so this really is the
  // AI jogging rather than sprinting the last two metres.
  const mag = clamp01(seekDist / (req.arriveRadius > 1e-6 ? req.arriveRadius : 1));

  // The clamp is not belt-and-braces: renormalizing above can land |dir| at
  // 1 + 1ulp, and moveX must be inside [-1, 1] for the wire quantizer (§2.8).
  out.x = clamp(bx * mag, -1, 1);
  out.z = clamp(bz * mag, -1, 1);
}
